import { Text } from "@solomon-apps/shared/src/components/ui/text";
import {
    CheckIcon,
    CrossIcon,
    PencilPaperIcon
} from "@solomon-labs/design/components/icons";
import { ButtonIcon, Input } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { DisabledReason } from "@/components/DisabledReason";
import { VIEW_ONLY_REASON } from "@/components/ViewOnly";
import {
    useCreateSavedDestination,
    useUpdateSavedDestination
} from "@/hooks/useClientSavedDestinations";
import { FormErrorRetry } from "@/pages/shared/FormErrorRetry";
import {
    isClientConnectionError,
    resolveClientFormError
} from "@/utils/clientFormErrors";

import {
    destinationDisplayName,
    toSavedDestinationInput
} from "./destinationInput";

interface DestinationNameCellProps {
    uuid: string;
    address: string;
    /** Custom label, or "" when the destination has none. */
    label: string;
    editing: boolean;
    viewOnly: boolean;
    /** False for a destination that is in use but not in the address book
     *  yet (e.g. a wallet's own address): renaming it saves it, named. */
    saved?: boolean;
    onOpen: () => void;
    /** Leave edit mode (saved, unchanged, or cancelled). */
    onClose: () => void;
    inputRef: (element: HTMLInputElement | null) => void;
    pencilRef: (element: HTMLButtonElement | null) => void;
}

/**
 * The Rewards Destinations table's Name cell, with in-place rename. Mirrors
 * the Distributor Console's per-field inline edit (yaas-issuer
 * `AccessMintingLimits`): the name (label, or the 4 + 4 address) with a
 * `PencilPaperIcon` right after it; the pencil turns the cell into a
 * light-bar input (DS `Input`, transparent surface, the row's own type) with
 * Save (✓)
 * and Cancel (✕). Enter saves, Escape cancels; the table owns which row is
 * open (one at a time) and where focus goes.
 */
export function DestinationNameCell(
    props: DestinationNameCellProps
): React.ReactElement {
    const { address, label, editing, viewOnly, onOpen, pencilRef } = props;
    const name = destinationDisplayName({ label, address });
    if (editing) return <LabelEditor {...props} name={name} />;
    return (
        <div className="flex min-w-0 items-center gap-2">
            <Text
                variant="fine-print"
                render={<span />}
                className="min-w-0 truncate text-foreground-base"
            >
                {name}
            </Text>
            <DisabledReason
                reason={viewOnly ? VIEW_ONLY_REASON : null}
                label={`Edit label for ${name}`}
            >
                <ButtonIcon
                    ref={pencilRef}
                    variant="transparent"
                    size="lg"
                    icon={<PencilPaperIcon />}
                    aria-label={`Edit label for ${name}`}
                    disabled={viewOnly}
                    onClick={onOpen}
                />
            </DisabledReason>
        </div>
    );
}

/**
 * The open cell: prefilled with the label (empty when unlabeled). Saves
 * through `useUpdateSavedDestination` (or, for a destination not in the
 * address book yet, saves it named via `useCreateSavedDestination`); an
 * empty label saves `name = address`
 * (unlabeled), and an unchanged one closes without a request. A failure keeps
 * the cell open with the reason under it, and Retry when the server was
 * unreachable.
 */
function LabelEditor({
    uuid,
    address,
    label,
    name,
    saved = true,
    onClose,
    inputRef
}: DestinationNameCellProps & { name: string }): React.ReactElement {
    const updateDestination = useUpdateSavedDestination();
    const createDestination = useCreateSavedDestination();
    const [value, setValue] = useState(label);
    const [error, setError] = useState<string | null>(null);
    const [retry, setRetry] = useState<(() => void) | null>(null);
    const saving = updateDestination.isPending || createDestination.isPending;
    const errorId = `destination-label-error-${uuid}`;

    const save = async (nextName: string): Promise<void> => {
        setError(null);
        setRetry(null);
        try {
            if (saved) {
                await updateDestination.mutateAsync({
                    uuid,
                    data: { name: nextName, address }
                });
            } else {
                await createDestination.mutateAsync({
                    name: nextName,
                    address
                });
            }
            onClose();
        } catch (e) {
            setError(
                resolveClientFormError(
                    e,
                    "savedDestination",
                    "Couldn't save the label."
                )
            );
            if (isClientConnectionError(e)) {
                setRetry(() => (): void => void save(nextName));
            }
        }
    };

    const commit = (): void => {
        if (saving) return;
        const next = toSavedDestinationInput(address, value).name;
        const current = label || address;
        if (next === current) {
            onClose();
            return;
        }
        void save(next);
    };

    return (
        <div className="flex min-w-0 flex-col">
            {/* The DS's shortest field (sm, 32px) is taller than the row's text
                line; -my-1.5 lets it sit in the row's own padding so opening
                an edit doesn't change the row height. */}
            <div className="-my-1.5 flex min-w-0 items-center gap-2">
                <Input
                    ref={inputRef}
                    surface="transparent"
                    size="sm"
                    textVariant="fine-print"
                    placeholderVariant="fine-print"
                    placeholder="Label (Optional)"
                    aria-label="Label (Optional)"
                    className="min-w-0 flex-1"
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    disabled={saving}
                    aria-describedby={error ? errorId : undefined}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            commit();
                        } else if (event.key === "Escape") {
                            event.preventDefault();
                            onClose();
                        }
                    }}
                />
                <div className="flex shrink-0 items-center gap-1">
                    <ButtonIcon
                        variant="transparent"
                        size="lg"
                        icon={<CheckIcon />}
                        aria-label={`Save label for ${name}`}
                        aria-busy={saving}
                        className={
                            saving ? "motion-safe:animate-pulse" : undefined
                        }
                        disabled={saving}
                        onClick={commit}
                    />
                    <ButtonIcon
                        variant="transparent"
                        size="lg"
                        icon={<CrossIcon />}
                        aria-label={`Cancel editing label for ${name}`}
                        disabled={saving}
                        onClick={onClose}
                    />
                </div>
            </div>
            {error ? (
                <div id={errorId}>
                    <FormErrorRetry
                        className="mt-1.5"
                        error={error}
                        onRetry={retry ?? undefined}
                        busy={saving}
                    />
                </div>
            ) : null}
        </div>
    );
}
